import { getActiveRelease, quoteTickets, formatMoney } from './mm-2027-pricing.js';
import { getProductUrgency } from './mm-2027-product-urgency.js';

const root = document.querySelector('[data-mm-product]');
if (root && !root.dataset.mmCommerce) {
  const select = selector => root.querySelector(selector);
  const quantityInput = select('#mm-product-quantity');
  const minus = select('[data-product-minus]');
  const plus = select('[data-product-plus]');
  const review = select('[data-product-review]');
  const ctaTotal = select('[data-product-cta-total]');
  const finalBuy = select('[data-product-buy]');
  const actionTotal = select('[data-product-action-total]');
  const actionPrice = select('[data-product-action-price]');
  const actionLabel = select('[data-product-action-label]');
  const actionQuantity = select('[data-product-action-quantity]');
  const urgency = select('[data-product-urgency]');
  const actionUrgency = select('[data-product-action-urgency]');
  let reviewTrigger = review;
  const error = select('#mm-product-error');
  const dialog = select('.mm-product-dialog');
  const ticketId = root.dataset.ticketId;
  const passLinks = [...root.querySelectorAll('[data-pass-link]')];
  const passPrices = [...root.querySelectorAll('[data-pass-price]')];
  let config;
  let currentQuote;
  let releaseTimer;
  const dateFormatter = new Intl.DateTimeFormat('en-GB', { dateStyle:'long', timeStyle:'short', timeZone:'Europe/London' });

  function updatePassLinks() {
    const quantity = Number(quantityInput.value);
    // Keep a valid Conference/VIP quantity when switching to Mastermind so its
    // lower limit is explained by validation, rather than silently changing it.
    const valid = quantityInput.value.trim() && Number.isInteger(quantity) && quantity >= 1 && quantity <= 100;
    for (const link of passLinks) {
      const destination = new URL(link.getAttribute('href'), window.location.href);
      if (valid) destination.searchParams.set('attendees', String(quantity));
      else destination.searchParams.delete('attendees');
      link.setAttribute('href', `${destination.pathname}${destination.search}${destination.hash}`);
    }
  }

  function update() {
    const now = Date.now();
    currentQuote = null;
    updatePassLinks();
    try {
      const { current, next } = getActiveRelease(config, now);
      window.clearTimeout(releaseTimer);
      if (next) releaseTimer = window.setTimeout(update, Math.min(Math.max(1, Date.parse(next.startsAt) - now), 2_147_483_647));
      select('[data-product-release]').textContent = current.label;
      select('[data-product-price]').textContent = formatMoney(current.prices[ticketId]);
      if (actionPrice) actionPrice.textContent = formatMoney(current.prices[ticketId]);
      if (actionLabel) actionLabel.textContent = current.label;
      if (urgency || actionUrgency) {
        const message = getProductUrgency(current, next, ticketId);
        if (urgency) urgency.textContent = message.short;
        if (actionUrgency) actionUrgency.textContent = message.detail;
      }
      for (const price of passPrices) {
        const pence = current.prices[price.dataset.passPrice];
        price.textContent = Number.isSafeInteger(pence) && pence >= 0 ? formatMoney(pence) : '—';
      }
      select('[data-product-next]').textContent = next
        ? `${next.label}: ${formatMoney(next.prices[ticketId])} per attendee from ${dateFormatter.format(new Date(next.startsAt))} (UK time).`
        : current.id === 'early-bird'
          ? 'Further price increases are planned. Next release date and price to be announced.'
          : 'This is the current ticket price. No further price release has been announced.';
      const quantity = Number(quantityInput.value);
      const maximumQuantity = Number(quantityInput.max);
      if (!quantityInput.value.trim() || !Number.isInteger(quantity) || quantity < 1 || quantity > maximumQuantity) {
        throw new TypeError(`Enter a whole number of attendees from 1 to ${maximumQuantity}.`);
      }
      const quote = quoteTickets(config, { ticketId, quantity, now });
      select('[data-product-total]').textContent = formatMoney(quote.total);
      if (ctaTotal) ctaTotal.textContent = formatMoney(quote.total);
      if (actionTotal) actionTotal.textContent = formatMoney(quote.total);
      if (actionQuantity) actionQuantity.textContent = `${quantity} ${quantity === 1 ? 'attendee' : 'attendees'}`;
      select('[data-product-discount-row]').hidden = !quote.discountPercent;
      select('[data-product-discount]').textContent = `${formatMoney(quote.discountTotal)} (${quote.discountPercent}%)`;
      const teamNote = select('[data-product-team-note]');
      if (teamNote) teamNote.textContent = quote.discountPercent
        ? `${quote.discountPercent}% agency discount included in this planning estimate.`
        : config.groupDiscounts?.enabled
          ? 'Add your attendees to see any eligible agency discount in the total.'
          : 'This preview shows the listed price before group savings. On the live site, save 10% on 3–4 Conference tickets or 15% on 5 or more in one order. Group savings cannot be combined with other discounts.';
      minus.disabled = quantity <= 1;
      plus.disabled = quantity >= maximumQuantity;
      review.disabled = false;
      if (finalBuy) finalBuy.disabled = false;
      quantityInput.removeAttribute('aria-invalid');
      error.hidden = true;
      currentQuote = quote;
      if (dialog.open) updateDialog();
    } catch (issue) {
      const configValid = config && (() => { try { getActiveRelease(config, now); return true; } catch { return false; } })();
      error.textContent = configValid ? issue.message : 'Ticket pricing is temporarily unavailable in this preview.';
      if (!configValid) {
        select('[data-product-release]').textContent = 'Pricing unavailable';
        select('[data-product-price]').textContent = '—';
        select('[data-product-next]').textContent = 'Pricing configuration needs review.';
        passPrices.forEach(price => { price.textContent = '—'; });
        if (actionPrice) actionPrice.textContent = '—';
        if (actionLabel) actionLabel.textContent = 'Pricing unavailable';
        if (urgency) urgency.textContent = '';
        if (actionUrgency) actionUrgency.textContent = 'Ticket pricing is temporarily unavailable in this preview.';
      }
      error.hidden = false;
      quantityInput.setAttribute('aria-invalid', 'true');
      select('[data-product-total]').textContent = '—';
      if (ctaTotal) ctaTotal.textContent = '—';
      if (actionTotal) actionTotal.textContent = '—';
      if (actionQuantity) actionQuantity.textContent = 'Check attendee count';
      select('[data-product-discount-row]').hidden = true;
      review.disabled = true;
      if (finalBuy) finalBuy.disabled = true;
      minus.disabled = false;
      plus.disabled = false;
      if (dialog.open) dialog.close();
    }
  }

  function updateDialog() {
    if (!currentQuote) return;
    select('[data-product-dialog-quantity]').textContent = `${currentQuote.quantity} ${currentQuote.quantity === 1 ? 'attendee' : 'attendees'} · ${currentQuote.release.label}`;
    select('[data-product-dialog-total]').textContent = formatMoney(currentQuote.total);
  }

  function changeQuantity(delta) {
    const value = Number(quantityInput.value);
    const maximumQuantity = Number(quantityInput.max);
    // A quantity carried across from another pass can exceed this pass's cap.
    // A deliberate minus/plus click corrects that value without resetting to one.
    const valid = quantityInput.value.trim() && Number.isInteger(value) && value >= 1;
    quantityInput.value = String(valid ? Math.max(1, Math.min(maximumQuantity, value + delta)) : 1);
    update();
  }

  const incomingQuantity = new URLSearchParams(window.location.search).get('attendees');
  if (incomingQuantity !== null && /^\d{1,3}$/.test(incomingQuantity)) {
    const quantity = Number(incomingQuantity);
    if (quantity >= 1 && quantity <= 100) quantityInput.value = String(quantity);
  }
  try { config = JSON.parse(root.dataset.mmConfig); } catch { config = null; }
  quantityInput.addEventListener('input', update);
  minus.addEventListener('click', () => changeQuantity(-1));
  plus.addEventListener('click', () => changeQuantity(1));
  function openReview(trigger) {
    update();
    if (!currentQuote) return;
    reviewTrigger = trigger;
    updateDialog();
    dialog.showModal();
  }
  review.addEventListener('click', () => openReview(review));
  if (finalBuy) finalBuy.addEventListener('click', () => openReview(finalBuy));
  root.querySelectorAll('[data-product-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('close', () => reviewTrigger.focus({ preventScroll:true }));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) update(); });
  update();
}
